import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Booking } from 'src/features/bookings/api/types/response/booking.response';
import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortOrder } from 'src/types/enum/sort-order';
import { SortController } from 'src/types/interfaces/sort-controller';
import { TableColumn } from 'src/types/interfaces/table-column';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';
import { BookingsTableRow } from '../../../molecules/employees-group-details/bookings/bookings-table-row';

interface Props {
  data: Booking[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
}

export const BookingsTable = ({
  data,
  isLoading,
  error,
  sortController,
  setSortController,
  handleChangeSort,
}: Props) => {
  const { t } = useTranslation(['bookings', 'common']);

  const columns: TableColumn[] = [
    {
      id: 'employeeBookerEmail',
      label: t('bookings:common.columns.employee'),
      isSortable: true,
    },
    {
      id: 'space',
      label: t('bookings:common.columns.space'),
      isSortable: false,
    },
    {
      id: 'from',
      label: t('bookings:common.columns.startEnd'),
      isSortable: true,
    },
    {
      id: 'presence',
      label: t('bookings:common.columns.presence'),
      isSortable: false,
    },
    {
      id: 'state',
      label: t('bookings:common.columns.state'),
      isSortable: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={sortController.sortBy === column.id}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.map(
              ({
                token,
                spaceName,
                spaceType,
                spaceFloor,
                employeeBookerEmail,
                from,
                to,
                status,
                confirmedAt,
              }) => (
                <BookingsTableRow
                  key={token}
                  employeeEmail={employeeBookerEmail}
                  spaceName={spaceName}
                  spaceType={spaceType}
                  spaceFloor={spaceFloor}
                  from={from}
                  to={to}
                  confirmedAt={confirmedAt}
                  status={status}
                />
              ),
            )}
          </TableBody>
        </Table>
      </Scrollbar>

      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
};
